import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useAuthStore from '../stores/authStore';
import { domainsAPI } from '../services/api';
import { Eye, EyeOff, Check, ShieldCheck } from 'lucide-react';

const DEFAULT_ALLOWED_DOMAINS = [
  '@tickmarks.net',
  '@tickandtie.com',
  '@artscentral.in',
  '@creativeconnects.org',
];

const Register = () => {
  const navigate = useNavigate();
  const { register, loading, error, clearError } = useAuthStore();
  const [allowedDomains, setAllowedDomains] = useState(DEFAULT_ALLOWED_DOMAINS);
  const [domainError, setDomainError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await domainsAPI.getPublic();
        if (res?.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setAllowedDomains(res.data.data);
        }
      } catch (err) {
        console.warn('Could not fetch public allowed domains, using defaults:', err);
      }
    };
    fetchDomains();
  }, []);

  const validateEmailDomain = (email) => {
    if (!email || !email.includes('@')) return false;
    const emailDomain = '@' + email.split('@').pop().trim().toLowerCase();
    return allowedDomains.some((d) => d.toLowerCase() === emailDomain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();
    setDomainError('');

    if (!validateEmailDomain(formData.email)) {
      setDomainError(
        `Account creation is only permitted with authorized domains: ${allowedDomains.join(', ')}`
      );
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    const result = await register({
      name: formData.name,
      email: formData.email,
      password: formData.password,
      roles: ['Member'],
    });

    if (result) {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Check size={32} color="white" />
            </div>
            <div className="login-logo" style={{ color: 'var(--success)' }}>Registration Successful!</div>
            <div className="login-subtitle">
              Please check your email to verify your account before signing in.
            </div>
          </div>
          <button
            className="btn btn-primary"
            style={{ width: '100%' }}
            onClick={() => navigate('/')}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
                <img src="/tickmarks-logo.png" alt="Tickmarks Logo" style={{ height: '40px' }} />
          </div>
          <div className="login-subtitle">Create your account</div>
        </div>

        {(error || domainError) && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '6px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: 'var(--error)',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px'
          }}>
            ⚠ {domainError || error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              className="form-input"
              placeholder="Enter your work email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (domainError) setDomainError('');
              }}
              required
            />
            <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                <ShieldCheck size={13} style={{ color: 'var(--primary)' }} />
                <span style={{ fontWeight: 500 }}>Permitted domains:</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {allowedDomains.map((dom) => (
                  <span
                    key={dom}
                    style={{
                      background: 'rgba(13, 148, 136, 0.1)',
                      color: 'var(--primary)',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                    }}
                  >
                    {dom}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                style={{ paddingRight: '48px' }}
              />
              <button
                type="button"
                className="btn-icon"
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <p className="text-secondary text-sm">
            Already have an account?{' '}
            <Link to="/" style={{ color: 'var(--primary)', fontWeight: 500 }}>
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;



